import { useCallback } from 'react';

type EditorBlock = {
  type: string;
  data: {
    text?: string;
    code?: string;
    style?: string;
    items?: ListItem[];
    level?: number;
    [key: string]: unknown;
  };
};

type ListItem = {
  content: string;
  meta: { checked?: boolean };
  items: ListItem[];
};

export const useEditorConverters = () => {
  const convertInlineHTMLToMarkdown = useCallback((html: string) => {
    if (!html) return '';

    // Convert HTML inline formatting to Markdown
    return html
      .replace(/<br>/gi, '\n') // Convert <br> tags to newlines
      .replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)')
      .replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**')
      .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**')
      .replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*')
      .replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*')
      .replace(/<mark[^>]*class="cdx-marker"[^>]*>(.*?)<\/mark>/gi, (_, content) => {
        // If content is already wrapped with [[]], keep it as is, otherwise add ==
        return content.startsWith('[[') && content.endsWith(']]') ? content : `==${content}==`;
      })
      .replace(/<[^>]*>/g, ''); // Remove any remaining HTML tags
  }, []);

  const convertMarkdownToHtml = useCallback((markdown: string) => {
    if (!markdown) return '';

    // Convert Markdown inline formatting to HTML
    return markdown
      .replace(/\n/g, '<br>')
      .replace(/\[([^\]]*)\]\(([^)]*)\)/g, '<a href="$2">$1</a>')
      .replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
      .replace(/\*(.*?)\*/g, '<i>$1</i>')
      .replace(/==\[\[(.*?)\]\]==/g, '[[$1]]') // Handle already inserted ==[[text]]== syntax
      .replace(/\[\[(.*?)\]\]/g, '<mark class="cdx-marker">[[$1]]</mark>') // Handle [[text]] syntax
      .replace(/==(.*?)==/g, '<mark class="cdx-marker">$1</mark>'); // Handle ==text== syntax
  }, []);

  const convertBlocksToMarkdown = useCallback(
    (blocks: EditorBlock[] | string) => {
      if (typeof blocks === 'string') {
        return blocks;
      }
      return blocks
        .map((block: EditorBlock) => {
          switch (block.type) {
            case 'paragraph': {
              return convertInlineHTMLToMarkdown(block.data.text || '');
            }

            case 'header': {
              const level = (block.data.level as number) || 2;
              const text = convertInlineHTMLToMarkdown(block.data.text || '');
              return `${'#'.repeat(level)} ${text}`;
            }

            case 'code': {
              return `\`\`\`\n${block.data.code || ''}\n\`\`\``;
            }

            case 'list': {
              const items = (block.data.items as ListItem[]) || [];
              const style = block.data.style as string;

              const convertListItems = (
                listItems: ListItem[],
                listStyle: string,
                indentLevel: number = 0
              ): string => {
                const indent = '  '.repeat(indentLevel);

                return listItems
                  .map((item, index) => {
                    const convertedText = convertInlineHTMLToMarkdown(item.content);
                    let result = '';

                    if (listStyle === 'checklist') {
                      result = `${indent}- [${item.meta.checked ? 'x' : ' '}] ${convertedText}`;
                    } else if (listStyle === 'ordered') {
                      result = `${indent}${index + 1}. ${convertedText}`;
                    } else {
                      result = `${indent}- ${convertedText}`;
                    }

                    // Handle nested items
                    if (item.items && item.items.length > 0) {
                      const nestedItems = convertListItems(item.items, listStyle, indentLevel + 1);
                      result += '\n' + nestedItems;
                    }

                    return result;
                  })
                  .join('\n');
              };

              return convertListItems(items, style);
            }

            default:
              return '';
          }
        })
        .filter(block => block.trim() !== '')
        .join('\n\n')
        .trim();
    },
    [convertInlineHTMLToMarkdown]
  );

  const debouncedConvertBlocksToMarkdown = useCallback(
    (blocks: EditorBlock[], callback: (markdown: string) => void) => {
      const timeoutId = setTimeout(() => {
        const markdown = convertBlocksToMarkdown(blocks);
        callback(markdown);
      }, 300);

      return () => clearTimeout(timeoutId);
    },
    [convertBlocksToMarkdown]
  );

  const convertMarkdownToEditorData = useCallback(
    (markdown: string) => {
      if (!markdown.trim()) {
        return {
          blocks: [
            {
              type: 'paragraph',
              data: {
                text: '',
              },
            },
          ],
        };
      }

      const lines = markdown.split('\n');
      const blocks: EditorBlock[] = [];
      let i = 0;

      const getListItemMatch = (line: string, listType: string) => {
        const trimmedLine = line.trim();
        if (listType === 'checklist') {
          return trimmedLine.match(/^-\s*\[([x\s])\]\s*(.*)$/);
        } else if (listType === 'ordered') {
          return trimmedLine.match(/^\d+\.\s(.*)$/);
        } else if (listType === 'unordered') {
          return trimmedLine.match(/^[-]\s(.*)$/);
        }
        return null;
      };

      const getIndentationLevel = (line: string): number => {
        const indentMatch = line.match(/^(\s*)/);
        return indentMatch ? Math.floor(indentMatch[1].length / 2) : 0;
      };

      const parseNestedList = (
        startIndex: number,
        listType: 'checklist' | 'ordered' | 'unordered'
      ): { items: ListItem[]; nextIndex: number } => {
        const items: ListItem[] = [];
        let currentIndex = startIndex;

        while (currentIndex < lines.length) {
          const line = lines[currentIndex];

          // Calculate indentation level
          const currentIndentLevel = getIndentationLevel(line);
          const baseIndentLevel = getIndentationLevel(lines[startIndex]);

          // If we've moved to a different list type or indentation level, stop parsing this list
          if (currentIndentLevel < baseIndentLevel) {
            break;
          }

          const match = getListItemMatch(line, listType);
          if (match) {
            const item: ListItem = {
              content: convertMarkdownToHtml(listType === 'checklist' ? match[2] : match[1]),
              meta: listType === 'checklist' ? { checked: match[1] === 'x' } : {},
              items: [],
            };

            currentIndex++;

            // Check for nested items
            if (currentIndex < lines.length) {
              const nextLine = lines[currentIndex];
              const nextIndentLevel = getIndentationLevel(nextLine);

              if (nextIndentLevel > currentIndentLevel) {
                const nestedResult = parseNestedList(currentIndex, listType);
                item.items = nestedResult.items;
                currentIndex = nestedResult.nextIndex;
              } else {
                // Handle continuation lines (added with Shift+Enter)
                while (currentIndex < lines.length) {
                  const continuationLine = lines[currentIndex];
                  const continuationMatch = getListItemMatch(continuationLine, listType);

                  // If it's a continuation line (has content but not a list item)
                  if (continuationLine.trim() && !continuationMatch) {
                    item.content += `\n${convertMarkdownToHtml(continuationLine)}`;
                    currentIndex++;
                  } else {
                    break;
                  }
                }
              }
            }

            items.push(item);
          } else {
            break;
          }
        }

        return { items, nextIndex: currentIndex };
      };

      while (i < lines.length) {
        const line = lines[i].trim();

        if (!line) {
          i++;
          continue;
        }

        // Code blocks
        if (line.startsWith('```')) {
          const codeLines: string[] = [];
          i++; // Skip opening ```
          while (i < lines.length && !lines[i].trim().startsWith('```')) {
            codeLines.push(lines[i]);
            i++;
          }
          blocks.push({
            type: 'code',
            data: {
              code: codeLines.join('\n'),
            },
          });
          i++; // Skip closing ```
          continue;
        }

        // Headers
        if (line.match(/^#{1,6}\s/)) {
          const headerMatch = line.match(/^(#{1,6})\s(.*)$/);
          if (headerMatch) {
            blocks.push({
              type: 'header',
              data: {
                level: headerMatch[1].length,
                text: convertMarkdownToHtml(headerMatch[2]),
              },
            });
            i++;
            continue;
          }
        }

        // Checklist items
        if (line.match(/^-\s*\[[x\s]\]/)) {
          const result = parseNestedList(i, 'checklist');
          blocks.push({
            type: 'list',
            data: {
              style: 'checklist',
              items: result.items,
            },
          });
          i = result.nextIndex;
          continue;
        }

        // Ordered list
        if (line.match(/^\d+\.\s/)) {
          const result = parseNestedList(i, 'ordered');
          blocks.push({
            type: 'list',
            data: {
              style: 'ordered',
              items: result.items,
            },
          });
          i = result.nextIndex;
          continue;
        }

        // Unordered list
        if (line.match(/^[-]\s/)) {
          const result = parseNestedList(i, 'unordered');
          blocks.push({
            type: 'list',
            data: {
              style: 'unordered',
              items: result.items,
            },
          });
          i = result.nextIndex;
          continue;
        }

        // Regular paragraph - collect consecutive lines into one block
        const paragraphLines: string[] = [];
        while (
          i < lines.length &&
          lines[i].trim() &&
          !lines[i].trim().startsWith('```') &&
          !lines[i].trim().match(/^#{1,6}\s/) &&
          !lines[i].trim().match(/^-\s*\[[x\s]\]/) &&
          !lines[i].trim().match(/^\d+\.\s/) &&
          !lines[i].trim().match(/^[-]\s/)
        ) {
          paragraphLines.push(lines[i]);
          i++;
        }

        if (paragraphLines.length > 0) {
          blocks.push({
            type: 'paragraph',
            data: {
              text: convertMarkdownToHtml(paragraphLines.join('\n')), // Join with newlines
            },
          });
        }
      }

      return { blocks };
    },
    [convertMarkdownToHtml]
  );

  const parseValueToEditorData = useCallback(
    (inputValue: string | EditorBlock[]) => {
      if (Array.isArray(inputValue)) {
        return inputValue;
      }

      if (!inputValue?.trim()) {
        return {
          blocks: [
            {
              type: 'paragraph',
              data: {
                text: '',
              },
            },
          ],
        };
      }

      // If value is already in EditorJS format, use it
      if (inputValue.startsWith('{')) {
        try {
          const parsed = JSON.parse(inputValue);
          if (parsed.blocks) {
            return parsed;
          }
        } catch (e) {
          console.error('Failed to parse EditorJS data:', e);
        }
      }

      // Parse as Markdown
      return convertMarkdownToEditorData(inputValue);
    },
    [convertMarkdownToEditorData]
  );

  return {
    convertInlineHTMLToMarkdown,
    convertMarkdownToHtml,
    convertBlocksToMarkdown,
    debouncedConvertBlocksToMarkdown,
    convertMarkdownToEditorData,
    parseValueToEditorData,
  };
};
